/**
 * #599 — la búsqueda de un material contra el CATÁLOGO, una sola vez. Mitad WEB.
 *
 * GEMELO EXACTO de `mcp-server/src/utils/inventorySearch.js`: el cuerpo de los dos
 * archivos —todo menos este comentario de cabecera— es idéntico byte a byte, y el
 * test de paridad (`mcp-server/tests/tools/inventorySearchLaneParity.test.js`) lo
 * verifica y además corre el MISMO dataset por las dos copias. Se duplica a mano,
 * igual que `toolSearch.js` (#567), `rawTextFidelity.js` (#369) y
 * `materialSplit.js` (#384), porque el bundle de CRA no resuelve imports fuera de
 * `src/`. Si cambiás uno, cambiá el otro.
 *
 * EN ESTE CARRIL lo consume `src/pages/technician/materials/SmartMaterialRequest.js`
 * (`matchItems`), que es el gemelo web de `match_inventory_items` y traía el MISMO
 * defecto que #599 midió en `check_materials_availability`: un solo ilike de
 * subcadena sobre la frase ENTERA, con lo cual la lista de candidatos volvía vacía
 * y el scoring difuso nunca llegaba a correr.
 *
 * El otro consumidor de #599 —`check_materials_availability` desde
 * `src/services/materialsAvailabilityService.js`— NO necesita esta copia: ese
 * servicio no calcula nada, delega en la tool por `callMcpTool`.
 */

// Why: ilike is case-insensitive but accent-sensitive AND matches punctuation
// literally. A search for "brida 2 pulgadas" never matches "Brida 2\" clase 150"
// (inch-quote, no word "pulgadas" in the row). Same pattern as listTools after
// commit 8627266: normalize both sides + token-AND matching with measurement
// stop-words filtered out, so the bridge inventory→tools flow can find the row
// via natural-language phrasing the user actually types.
export const INV_STOP_WORDS = new Set([
  'de','del','para','con','por','sin','el','la','los','las','un','una','unos','unas',
  'pulgada','pulgadas','pulg','in',
  'metro','metros','m','cm','mm',
  'kilo','kilos','kg','gramo','gramos','g',
  'litro','litros','l','ml'
]);

export const normalizeForInventorySearch = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    // Replace inch marks / quotes / parens / hyphens with spaces so they don't bind tokens
    .replace(/["'()\-_/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const tokenizeForInventorySearch = (s) =>
  normalizeForInventorySearch(s)
    .split(' ')
    .filter(t => t.length > 0 && !INV_STOP_WORDS.has(t));

/**
 * El haystack del MATCHER: sólo `name`.
 *
 * Deliberadamente más angosto que el de `query_inventory`. `scoreMatch()` puntúa
 * contra `name` y nada más, así que un candidato que entró por su categoría o su
 * SKU llegaría al scoring con 0 y sólo agregaría ruido al top-5.
 */
export const inventoryNameHaystack = (item) => normalizeForInventorySearch(item && item.name);

/**
 * El haystack de `query_inventory`: nombre, SKU, categoría y unidad. Es el
 * contrato que esa tool ya tenía; no se toca.
 */
export const inventorySearchHaystack = (item) =>
  normalizeForInventorySearch(
    [item && item.name, item && item.sku, item && item.category, item && item.unit]
      .filter(Boolean)
      .join(' ')
  );

/**
 * Token-AND: cada término con contenido de lo que dijo el usuario tiene que
 * aparecer en el haystack normalizado de la fila.
 */
export const filterByInventoryTokens = (rows, tokens, haystack = inventorySearchHaystack) =>
  (rows || []).filter((row) => {
    const hay = haystack(row);
    return tokens.every((tok) => hay.includes(tok));
  });

/**
 * Candidatos del catálogo para un término libre, para que `scoreMatch()` los
 * ordene. PURA: recibe las filas ya traídas por el carril que la llama.
 *
 * Un término que se queda SIN tokens con contenido ("de la", "") devuelve CERO
 * candidatos, no el catálogo entero. Sin esa guarda, el ítem sin nombre útil
 * matchearía contra la primera fila que saliera y el faltante se calcularía
 * contra el stock de un material que nadie pidió.
 *
 * @param {Array<object>} rows   filas del catálogo ya traídas (y ya filtradas por is_active).
 * @param {string} term          el nombre tal como lo dictó la persona.
 * @param {(row:object)=>string} [haystack]
 * @returns {Array<object>}
 */
export const findInventoryCandidates = (rows, term, haystack = inventoryNameHaystack) => {
  const tokens = tokenizeForInventorySearch(term);
  if (tokens.length === 0) return [];
  return filterByInventoryTokens(rows, tokens, haystack);
};
